/*
 * The Marketing suite's tabs (draft 12): Google profile, Ranks, Ads, Social and Cold email. Nothing posts to Google or
 * Meta and no cold email goes out in this version: approved replies and posts wait as held, and each cold email is kept
 * as a test.
 */
import { useState, type ReactNode } from "react";
import {
  CHANNEL_WORDS,
  wallToIso,
  type FinderRow,
  type GbpPostRow,
  type ProspectRow,
  type ReviewRow,
  type SequenceRow,
  type WebRankRow,
} from "@steward/shared";
import {
  useAddProspect,
  useAddSequence,
  useAds,
  useAiLines,
  useBriefs,
  useCancelGbpPost,
  useEnroll,
  useFind,
  useFinder,
  useGbp,
  useGbpPost,
  useMakeBrief,
  useMedia,
  useOutreach,
  useProspectReply,
  useRefreshWeb,
  useRescore,
  useReviewReply,
  useSending,
  useSetKeywords,
  useSocial,
  useSyncGbp,
  useWebRank,
} from "../api/marketing";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { InputText } from "../components/fields";
import { momentIn, money, plural, shortDate, todayIn } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { ago, Banner, Button, Card, LoadError, Skeleton, StatusLabel, Switch, type Status } from "../components/parts";
import { Social, Thumb } from "./SocialPosts";
import { ProfileEditor } from "./GoogleProfiles";
import { ProfileCard } from "./MarketingConnect";
import { MapRank } from "./MapRank";

const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");

function useRun() {
  const toast = useToast();
  return async (work: () => Promise<unknown>, done?: string) => {
    try {
      await work();
      if (done) toast(done);
      return true;
    } catch (e) {
      toast(errText(e), { kind: "error" });
      return false;
    }
  };
}

function TextArea({
  label,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="field wide">
      <span className="label">{label}</span>
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="tile">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      <span className="hint">{hint}</span>
    </div>
  );
}

// Google profile -------------------------------------------------------------------------------------------------------

const REPLY_WORDS: Record<ReviewRow["replyStatus"], [string, Status]> = {
  none: ["No reply", "warning"],
  draft: ["Draft", "informative"],
  approved: ["Approved, waiting to post", "informative"],
  posted: ["Posted", "success"],
};

export function GoogleProfile() {
  const { company, can } = useSession();
  const q = useGbp(company.id);
  const sync = useSyncGbp();
  const run = useRun();
  const edit = can("marketing.update");
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={6} />;
  const a = q.data;
  // 2026-11-01 owner: one listing card on top; the posting switches moved to Marketing settings and what is left to set up
  // to Needs you on the Overview.
  if (!a.profile)
    return (
      <div className="stack">
        <ProfileCard />
        <ProfileEditor />
        <MapRank />
      </div>
    );
  const p = a.profile;
  return (
    <div className="stack">
      <ProfileCard />
      <div className="tiles">
        <Tile
          label="Rating on Google"
          value={p.rating === null ? "None yet" : p.rating.toFixed(1)}
          hint={plural(p.reviewCount, "review")}
        />
        <Tile label="Waiting for a reply" value={String(a.waitingReplies)} hint="Reviews with no approved reply" />
        <Tile label="Last read" value={p.syncedAt ? ago(p.syncedAt) : "Never"} hint="Read again each night" />
      </div>
      <ProfileEditor />
      <Card
        title="Reviews"
        flush
        action={
          edit ? (
            <Button
              type="tertiary"
              size="small"
              loading={sync.isPending}
              onClick={() => void run(() => sync.mutateAsync(undefined), "Reviews read")}
            >
              Read now
            </Button>
          ) : null
        }
      >
        {a.reviews.length === 0 ? (
          <p className="muted pad">No reviews yet.</p>
        ) : (
          <ReviewList reviews={a.reviews} canEdit={edit} />
        )}
      </Card>
      <ProfilePosts posts={a.posts} canEdit={edit} />
      <MapRank />
    </div>
  );
}

const REVIEWS_A_PAGE = 3;

/** Reviews still waiting for a reply all show on top; the rest 3 at a time (owner, 2026-11-02: "scroll forever"). */
function ReviewList({ reviews, canEdit }: { reviews: ReviewRow[]; canEdit: boolean }) {
  const [page, setPage] = useState(0);
  const waiting = reviews.filter((r) => r.replyStatus === "none" || r.replyStatus === "draft");
  const rest = reviews.filter((r) => r.replyStatus !== "none" && r.replyStatus !== "draft");
  const pages = Math.max(1, Math.ceil(rest.length / REVIEWS_A_PAGE));
  const at = Math.min(page, pages - 1);
  const from = at * REVIEWS_A_PAGE;
  return (
    <>
      {waiting.map((r) => (
        <Review key={r.id} r={r} canEdit={canEdit} />
      ))}
      {rest.slice(from, from + REVIEWS_A_PAGE).map((r) => (
        <Review key={r.id} r={r} canEdit={canEdit} />
      ))}
      {rest.length > REVIEWS_A_PAGE ? (
        <div className="row between pad">
          <span className="muted small">
            {from + 1} to {Math.min(from + REVIEWS_A_PAGE, rest.length)} of {rest.length} replied
          </span>
          <span className="row tight">
            <Button type="tertiary" size="small" disabled={at === 0} onClick={() => setPage(at - 1)}>
              Newer
            </Button>
            <Button type="secondary" size="small" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>
              Older
            </Button>
          </span>
        </div>
      ) : null}
    </>
  );
}

function Review({ r, canEdit }: { r: ReviewRow; canEdit: boolean }) {
  const [reply, setReply] = useState(r.reply ?? r.suggested);
  const save = useReviewReply();
  const run = useRun();
  const [label, status] = REPLY_WORDS[r.replyStatus];
  return (
    <div className="mk-review">
      <div className="row between">
        <b>
          {"★".repeat(r.stars)}
          <span className="muted">{"★".repeat(5 - r.stars)}</span> {r.author}
        </b>
        <span className="row tight">
          {r.clientId ? <StatusLabel status="inactive">Client</StatusLabel> : null}
          <StatusLabel status={status}>{label}</StatusLabel>
        </span>
      </div>
      <p>{r.text || <span className="muted">No words, just stars.</span>}</p>
      <span className="muted small">{shortDate(r.postedAt.slice(0, 10))}</span>
      {canEdit && r.replyStatus !== "posted" ? (
        <div className="stack">
          <TextArea label="Reply" value={reply} onChange={setReply} rows={2} />
          <div className="row end">
            <Button
              type="tertiary"
              size="small"
              onClick={() => void run(() => save.mutateAsync({ id: r.id, reply, approve: false }), "Draft saved")}
            >
              Save draft
            </Button>
            <Button size="small" onClick={() => void run(() => save.mutateAsync({ id: r.id, reply, approve: true }), "Approved")}>
              Approve
            </Button>
          </div>
        </div>
      ) : r.reply ? (
        <p className="mk-reply">{r.reply}</p>
      ) : null}
    </div>
  );
}

function PhotoPick({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  const { company } = useSession();
  const m = useMedia(company.id);
  const media = (m.data?.media ?? []).filter((x) => !x.blocked);
  if (media.length === 0) return <p className="muted">Add crew photos in Social first. Posts use real photos from jobs.</p>;
  return (
    <div className="mk-photos" role="radiogroup" aria-label="Photo">
      {media.map((x) => (
        <button
          key={x.id}
          type="button"
          role="radio"
          aria-checked={value === x.id}
          className="mk-pick"
          onClick={() => onChange(value === x.id ? null : x.id)}
        >
          <Thumb fileId={x.fileId} alt={x.caption || "Job photo"} />
        </button>
      ))}
    </div>
  );
}

/** One post on the Scheduled or Posted list: made here, or a Social post that also goes to the Google profile. */
interface ProfileItem {
  id: string;
  title: string;
  body: string;
  fileId: string | null;
  at: string | null;
  status: GbpPostRow["status"];
  fromSocial: boolean;
}

const POST_STATUS: Record<GbpPostRow["status"], [string, Status]> = {
  draft: ["Draft", "inactive"],
  scheduled: ["Scheduled", "informative"],
  held: ["Waiting", "warning"],
  posted: ["Posted", "success"],
};

const EMPTY_POST = { service: "", area: "", body: "", mediaId: null as string | null, day: "", time: "10:00" };

function ProfilePosts({ posts, canEdit }: { posts: GbpPostRow[]; canEdit: boolean }) {
  const { company } = useSession();
  const media = useMedia(company.id).data?.media ?? [];
  const social = useSocial(company.id).data?.posts ?? [];
  const [tab, setTab] = useState<"new" | "scheduled" | "posted">(canEdit ? "new" : "scheduled");
  const [d, setD] = useState(EMPTY_POST);
  const add = useGbpPost();
  const cancel = useCancelGbpPost();
  const run = useRun();
  const fileOf = (id: string | null | undefined) => media.find((m) => m.id === id)?.fileId ?? null;
  const items: ProfileItem[] = [
    ...posts.map((p) => ({
      id: p.id,
      title: `${p.service} in ${p.area}`,
      body: p.body,
      fileId: fileOf(p.mediaId),
      at: p.status === "posted" ? p.postedAt : p.status === "scheduled" ? p.scheduledFor : p.createdAt,
      status: p.status,
      fromSocial: false,
    })),
    ...social
      .filter((p) => p.platforms.includes("google") && (p.status === "scheduled" || p.results.google?.ok))
      .map((p): ProfileItem => ({
        id: p.id,
        title: p.body.split("\n")[0] ?? "",
        body: p.body.split("\n").slice(1).join("\n").trim(),
        fileId: fileOf(p.mediaIds[0]),
        at: p.results.google?.ok ? p.results.google.at : p.scheduledFor,
        status: p.results.google?.ok ? "posted" : "scheduled",
        fromSocial: true,
      })),
  ];
  const waiting = items.filter((p) => p.status !== "posted").sort((a, b) => (a.at ?? "").localeCompare(b.at ?? ""));
  const posted = items.filter((p) => p.status === "posted").sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
  const shown = tab === "posted" ? posted : waiting;
  const toast = useToast();
  const toastFor = (s: GbpPostRow["status"]) => {
    toast(
      s === "posted" ? "Posted to Google" : s === "scheduled" ? "Post scheduled" : "Post saved. It waits until posting is on.",
    );
    setTab(s === "posted" ? "posted" : "scheduled");
  };
  const save = () =>
    void run(async () => {
      const row = await add.mutateAsync({
        service: d.service,
        area: d.area,
        body: d.body,
        mediaId: d.mediaId,
        scheduledFor: d.day ? wallToIso(d.day, d.time || "10:00", company.timezone) : null,
      });
      setD(EMPTY_POST);
      toastFor(row.status);
    });
  const tabs = [
    ...(canEdit ? ([["new", "New post"]] as const) : []),
    ["scheduled", `Scheduled (${String(waiting.length)})`],
    ["posted", `Posted (${String(posted.length)})`],
  ] as const;
  return (
    <Card title="Profile posts">
      <div className="stack">
        <div className="segmented" role="tablist" aria-label="Profile posts">
          {tabs.map(([v, label]) => (
            <button key={v} type="button" role="tab" aria-selected={tab === v} onClick={() => setTab(v)}>
              {label}
            </button>
          ))}
        </div>
        {tab === "new" && canEdit ? (
          <>
            <p className="muted">The service, the area and what they get. One real photo. No prices or phone numbers.</p>
            <div className="mk-two">
              <InputText label="Service" value={d.service} onChange={(e) => setD({ ...d, service: e.target.value })} />
              <InputText label="Area" value={d.area} onChange={(e) => setD({ ...d, area: e.target.value })} />
            </div>
            <TextArea label="Post" value={d.body} onChange={(v) => setD({ ...d, body: v })} />
            <PhotoPick value={d.mediaId} onChange={(mediaId) => setD({ ...d, mediaId })} />
            <div className="mk-two">
              <InputText
                label="Day"
                type="date"
                min={todayIn(company.timezone)}
                value={d.day}
                onChange={(e) => setD({ ...d, day: e.target.value })}
              />
              <InputText
                label="Time"
                type="time"
                step={300}
                value={d.time}
                disabled={!d.day}
                onChange={(e) => setD({ ...d, time: e.target.value })}
              />
            </div>
            <span className="muted small">Leave the day empty to post now.</span>
            <div className="row end">
              {d.day ? (
                <Button type="tertiary" onClick={() => setD({ ...d, day: "" })}>
                  Post now instead
                </Button>
              ) : null}
              <Button loading={add.isPending} onClick={save}>
                {d.day ? "Schedule post" : "Post now"}
              </Button>
            </div>
          </>
        ) : shown.length === 0 ? (
          <p className="muted">{tab === "posted" ? "Nothing posted yet." : "Nothing scheduled."}</p>
        ) : null}
        {tab !== "new"
          ? shown.map((p) => {
              const when = p.at ? momentIn(p.at, company.timezone) : null;
              const [label, status] = POST_STATUS[p.status];
              return (
                <div key={p.id} className="mk-post">
                  {p.fileId ? <Thumb fileId={p.fileId} alt="Post photo" /> : <span className="mk-thumb" />}
                  <div className="stack tight">
                    <div className="row between">
                      <b>{p.title}</b>
                      <span className="row tight">
                        {p.fromSocial ? <StatusLabel status="inactive">From Social</StatusLabel> : null}
                        <StatusLabel status={status}>{label}</StatusLabel>
                      </span>
                    </div>
                    {when ? (
                      <span className="muted small" title={when.full}>
                        {p.status === "posted" ? "Posted" : p.status === "scheduled" ? "Goes out" : "Saved"} {when.day},{" "}
                        {when.time}
                      </span>
                    ) : null}
                    <p className="mk-pre">{p.body}</p>
                    {canEdit && p.status === "scheduled" && !p.fromSocial ? (
                      <div className="row end">
                        <Button
                          type="tertiary"
                          size="small"
                          loading={cancel.isPending && cancel.variables === p.id}
                          onClick={() => void run(() => cancel.mutateAsync(p.id), "Post canceled")}
                        >
                          Cancel post
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          : null}
      </div>
    </Card>
  );
}

// Ranks ----------------------------------------------------------------------------------------------------------------

/** Website rank and the page optimizer. Map rank is the Google profile's (owner, 2026-11-01: "move google profile rank
 * checker inside google profile, since that's a feature belonging to that"). */
export function Ranks() {
  return (
    <div className="stack">
      <WebRank />
      <PageOptimizer />
    </div>
  );
}

function WebRank() {
  const { company, can } = useSession();
  const q = useWebRank(company.id);
  const save = useSetKeywords();
  const refresh = useRefreshWeb();
  const run = useRun();
  const [words, setWords] = useState<string | null>(null);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={4} />;
  const a = q.data;
  const cols = [
    { key: "k", header: "Search", render: (r: WebRankRow) => r.keyword, sort: (r: WebRankRow) => r.keyword },
    {
      key: "p",
      header: "Place",
      align: "right" as const,
      render: (r: WebRankRow) => (r.position === null ? "Not seen" : r.position.toFixed(1)),
      sort: (r: WebRankRow) => r.position ?? 999,
    },
    {
      key: "c",
      header: "Change",
      align: "right" as const,
      render: (r: WebRankRow) =>
        r.change === null ? (
          ""
        ) : (
          <span className={r.change > 0 ? "mk-up" : r.change < 0 ? "mk-down" : ""}>
            {r.change > 0 ? `Up ${String(r.change)}` : r.change < 0 ? `Down ${String(-r.change)}` : "Same"}
          </span>
        ),
    },
    {
      key: "cl",
      header: "Clicks",
      align: "right" as const,
      render: (r: WebRankRow) => r.clicks,
      sort: (r: WebRankRow) => r.clicks,
    },
    {
      key: "i",
      header: "Seen",
      align: "right" as const,
      render: (r: WebRankRow) => r.impressions,
      sort: (r: WebRankRow) => r.impressions,
    },
  ];
  return (
    <Card
      title="Website rank"
      action={
        can("marketing.update") && a.site && a.connected ? (
          <Button
            type="tertiary"
            size="small"
            loading={refresh.isPending}
            onClick={() => void run(() => refresh.mutateAsync(undefined), "Read")}
          >
            Read now
          </Button>
        ) : null
      }
    >
      <div className="stack">
        {!a.connected ? (
          <Banner>Search Console is not connected yet. Your keywords are saved and fill in once it is.</Banner>
        ) : null}
        {!a.site ? (
          <Banner type="warning">Add the website in Settings, Website, so Search Console can be read.</Banner>
        ) : (
          <p className="muted">
            {a.site}. The last 28 days ({shortDate(a.windows.current.from)} to {shortDate(a.windows.current.to)}) against the 28
            before.
          </p>
        )}
        <DataTable title="Keywords you track" rows={a.tracked} columns={cols} rowKey={(r) => r.keyword} empty="No keywords yet" />
        {can("marketing.update") ? (
          words === null ? (
            <div className="row">
              <Button type="secondary" size="small" onClick={() => setWords(a.tracked.map((t) => t.keyword).join("\n"))}>
                Change keywords
              </Button>
            </div>
          ) : (
            <div className="stack">
              <TextArea label="One keyword on each line, 25 at most" value={words} onChange={setWords} rows={5} />
              <div className="row end">
                <Button type="tertiary" onClick={() => setWords(null)}>
                  Cancel
                </Button>
                <Button
                  loading={save.isPending}
                  onClick={() =>
                    void run(async () => {
                      await save.mutateAsync(
                        words
                          .split("\n")
                          .map((w) => w.trim())
                          .filter(Boolean),
                      );
                      setWords(null);
                    }, "Saved")
                  }
                >
                  Save keywords
                </Button>
              </div>
            </div>
          )
        ) : null}
        {a.top.length ? (
          <DataTable title="Top searches you don't track" rows={a.top} columns={cols} rowKey={(r) => r.keyword} />
        ) : null}
      </div>
    </Card>
  );
}

function PageOptimizer() {
  const { company, can } = useSession();
  const q = useBriefs(company.id);
  const make = useMakeBrief();
  const rescore = useRescore();
  const run = useRun();
  const [d, setD] = useState({ url: "", keyword: "" });
  return (
    <Card title="Page optimizer">
      <div className="stack">
        <p className="muted">
          What the top pages for a keyword cover, and a score for your page from 0 to 100. Prices are left out.
        </p>
        {can("marketing.update") ? (
          <div className="mk-two">
            <InputText label="Page address" value={d.url} onChange={(e) => setD({ ...d, url: e.target.value })} />
            <InputText label="Keyword" value={d.keyword} onChange={(e) => setD({ ...d, keyword: e.target.value })} />
            <Button loading={make.isPending} onClick={() => void run(() => make.mutateAsync(d), "Scored")}>
              Score this page
            </Button>
          </div>
        ) : null}
        {(q.data?.briefs ?? []).map((b) => (
          <details key={b.id} className="mk-brief">
            <summary>
              <span className={`mk-score ${b.score >= 80 ? "top" : b.score >= 50 ? "mid" : "low"}`}>{b.score}</span>
              <span>
                <b>{b.keyword}</b> <span className="muted">{b.url}</span>
              </span>
            </summary>
            <div className="stack">
              {b.parts.map((p) => (
                <div key={p.name} className="row between">
                  <span>{p.name}</span>
                  <span className="muted">{p.tip ?? "Good"}</span>
                  <b>{p.got}%</b>
                </div>
              ))}
              <span className="muted">
                Words to use{" "}
                {b.brief.terms
                  .slice(0, 12)
                  .map((t) => t.term)
                  .join(", ")}
              </span>
              {b.brief.questions.length ? (
                <span className="muted">Questions to answer {b.brief.questions.slice(0, 5).join(" ")}</span>
              ) : null}
              {can("marketing.update") ? (
                <div className="row">
                  <Button type="tertiary" size="small" onClick={() => void run(() => rescore.mutateAsync(b.id), "Scored again")}>
                    Score again
                  </Button>
                </div>
              ) : null}
            </div>
          </details>
        ))}
      </div>
    </Card>
  );
}

// Ads ------------------------------------------------------------------------------------------------------------------

const PLATFORM_WORDS = { gads: "Google Ads", lsa: "Local Services", facebook: "Facebook and Instagram" } as const;

export function Ads() {
  const { company } = useSession();
  const q = useAds(company.id);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={6} />;
  const a = q.data;
  return (
    <div className="stack">
      {!a.spendConnected ? (
        <Banner>Ad spend is not connected yet. Spend shows here once Google Ads and Meta are connected.</Banner>
      ) : null}
      <div className="tiles">
        {a.spend.map((s) => (
          <Tile
            key={s.platform}
            label={PLATFORM_WORDS[s.platform]}
            value={money(s.spendCents)}
            hint={`${plural(s.clicks, "click")}, ${plural(s.leads, "lead")} in 30 days`}
          />
        ))}
      </div>
      <DataTable
        title="How clients find you"
        rows={a.channels}
        rowKey={(r) => r.channel}
        empty="No leads, clients or spend in the last 30 days"
        columns={[
          { key: "c", header: "Channel", render: (r) => CHANNEL_WORDS[r.channel], sort: (r) => CHANNEL_WORDS[r.channel] },
          { key: "l", header: "Leads", align: "right", render: (r) => r.leads, sort: (r) => r.leads },
          { key: "n", header: "New clients", align: "right", render: (r) => r.newClients, sort: (r) => r.newClients },
          { key: "r", header: "Money in", align: "right", render: (r) => money(r.revenueCents), sort: (r) => r.revenueCents },
          { key: "s", header: "Spend", align: "right", render: (r) => money(r.spendCents), sort: (r) => r.spendCents },
          {
            key: "b",
            header: "Back",
            align: "right",
            render: (r) => (r.timesBack === null ? "No spend" : `${String(r.timesBack)}x`),
            sort: (r) => r.timesBack ?? -1,
          },
        ]}
      />
      <Card title="Booked and paid, sent back to the ads">
        <div className="stack">
          {!a.conversionsLive ? (
            <Banner>
              Sending to Meta and Google is off in this version. Each one waits as held. Only hashed email and phone go, never
              names.
            </Banner>
          ) : null}
          {a.conversions.length === 0 ? (
            <p className="muted">Nothing booked or paid in the last 7 days.</p>
          ) : (
            a.conversions.map((c) => (
              <div key={c.id} className="row between">
                <span>
                  {c.event === "booked" ? "Booked" : "Paid"} {money(c.valueCents)} to{" "}
                  {c.platform === "meta" ? "Meta" : "Google Ads"}
                </span>
                <span className="row tight">
                  <span className="muted small">{shortDate(c.eventAt.slice(0, 10))}</span>
                  <StatusLabel status={c.status === "sent" ? "success" : c.status === "failed" ? "critical" : "informative"}>
                    {c.status === "sent" ? "Sent" : c.status === "failed" ? "Failed" : "Held"}
                  </StatusLabel>
                </span>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

// Social ---------------------------------------------------------------------------------------------------------------

// Cold email -----------------------------------------------------------------------------------------------------------

const PROSPECT_WORDS: Record<ProspectRow["status"], [string, Status]> = {
  new: ["New", "inactive"],
  active: ["Emailing", "informative"],
  replied: ["Replied", "success"],
  done: ["Finished", "inactive"],
  unsub: ["Said stop", "critical"],
  bounced: ["Bounced", "critical"],
  paused: ["On hold", "warning"],
};

export function ColdEmail() {
  const { company, can } = useSession();
  const q = useOutreach(company.id);
  const enroll = useEnroll();
  const ai = useAiLines();
  const run = useRun();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [newSeq, setNewSeq] = useState(false);
  const [newOne, setNewOne] = useState(false);
  const [replyTo, setReplyTo] = useState<ProspectRow | null>(null);
  const edit = can("marketing.update");
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Skeleton rows={6} />;
  const a = q.data;
  const firstSeq = a.sequences[0];
  return (
    <div className="stack">
      <Banner>
        Cold email goes only from the separate outreach domain, never from a brand's main email. Only an owner turns sending on.
        In this version each email is kept as a test and nothing goes out.
      </Banner>
      <Card
        title="Sequences"
        action={
          edit ? (
            <Button type="secondary" size="small" icon="plus" onClick={() => setNewSeq(true)}>
              New sequence
            </Button>
          ) : null
        }
      >
        {a.sequences.length === 0 ? (
          <p className="muted">No sequences yet.</p>
        ) : (
          a.sequences.map((s) => <Sequence key={s.id} s={s} />)
        )}
      </Card>
      <DataTable
        title="Businesses to email"
        rows={a.prospects}
        rowKey={(p) => p.id}
        search={(p) => `${p.business} ${p.email} ${p.category ?? ""}`}
        {...(edit ? { selected: picked, onSelect: setPicked } : {})}
        tools={
          edit ? (
            <Button type="secondary" size="small" icon="plus" onClick={() => setNewOne(true)}>
              Add a business
            </Button>
          ) : undefined
        }
        bulk={
          <>
            {firstSeq ? (
              <Button
                size="small"
                onClick={() =>
                  void run(async () => {
                    const r = await enroll.mutateAsync({ sequenceId: firstSeq.id, prospectIds: [...picked] });
                    setPicked(new Set());
                    return r;
                  }, `Put on ${firstSeq.name}`)
                }
              >
                Put on {firstSeq.name}
              </Button>
            ) : null}
            <Button
              size="small"
              type="secondary"
              disabled={!a.aiLinesAllowed || picked.size > 10}
              onClick={() => void run(() => ai.mutateAsync([...picked]), "First lines written")}
            >
              Write first lines
            </Button>
          </>
        }
        empty="No businesses yet. Use Find businesses below or add one."
        columns={[
          {
            key: "b",
            header: "Business",
            render: (p) => (
              <span className="stack tight">
                <b>{p.business}</b>
                <span className="muted small">{p.email}</span>
              </span>
            ),
            sort: (p) => p.business,
          },
          { key: "l", header: "First line", render: (p) => p.aiLine ?? <span className="muted">None</span> },
          {
            key: "s",
            header: "Status",
            render: (p) => (
              <span className="stack tight">
                <StatusLabel status={PROSPECT_WORDS[p.status][1]}>{PROSPECT_WORDS[p.status][0]}</StatusLabel>
                {p.note ? <span className="muted small">{p.note}</span> : null}
              </span>
            ),
            sort: (p) => p.status,
          },
          {
            key: "r",
            header: "",
            render: (p) =>
              edit && p.status === "active" ? (
                <Button type="tertiary" size="small" onClick={() => setReplyTo(p)}>
                  They replied
                </Button>
              ) : null,
          },
        ]}
      />
      {!a.aiLinesAllowed ? (
        <Banner type="warning">AI first lines are a paid add-on. Ask head office to turn it on.</Banner>
      ) : null}
      <Finder allowed={a.finderAllowed} />
      {a.sent.length ? (
        <Card title="Emails made" flush>
          {a.sent.map((m) => (
            <div key={m.id} className="row between pad">
              <span>
                {m.business}, email {m.step + 1}. {m.subject}
              </span>
              <span className="muted small">{m.status === "dry_run" ? "Test, not sent" : m.status}</span>
            </div>
          ))}
        </Card>
      ) : null}
      <NewSequence open={newSeq} onClose={() => setNewSeq(false)} />
      <NewProspect open={newOne} onClose={() => setNewOne(false)} />
      <ReplyModal p={replyTo} onClose={() => setReplyTo(null)} />
    </div>
  );
}

function Sequence({ s }: { s: SequenceRow }) {
  const { can } = useSession();
  const sending = useSending();
  const run = useRun();
  return (
    <div className="mk-review">
      <div className="row between">
        <b>{s.name}</b>
        {can("outreach.send") ? (
          <span className="row tight">
            <span className="muted small">Sending</span>
            <Switch
              checked={s.sending}
              label={`Sending for ${s.name}`}
              onChange={(on) => void run(() => sending.mutateAsync({ id: s.id, on }), on ? "Sending is on" : "Sending is off")}
            />
          </span>
        ) : (
          <StatusLabel status={s.sending ? "success" : "inactive"}>{s.sending ? "Sending" : "Off"}</StatusLabel>
        )}
      </div>
      <span className="muted">
        From {s.fromName}, {s.fromEmail}. {plural(s.steps.length, "email")}. Today {s.sentToday} of {s.todayCap}
        {s.ramp && s.todayCap < s.dailyCap ? ` (warming up to ${String(s.dailyCap)})` : ""}.
      </span>
      <span className="muted small">
        {s.prospects.active} emailing, {s.prospects.replied} replied, {s.prospects.done} finished, {s.prospects.stopped} stopped
      </span>
    </div>
  );
}

function NewSequence({ open, onClose }: { open: boolean; onClose: () => void }) {
  const add = useAddSequence();
  const run = useRun();
  const [d, setD] = useState({
    name: "",
    fromName: "",
    fromEmail: "",
    mailingAddress: "",
    s1: "Cleaning for {{business}}",
    b1: "Hi {{first_name|there}},\n\n{{opener|Quick question about your office.}} Would a weekly cleaning help?",
    b2: "Following up on my note. Happy to stop by for a quick look.",
    cap: "30",
  });
  const f = (k: keyof typeof d) => (v: string) => setD({ ...d, [k]: v });
  return (
    <Modal
      title="New sequence"
      open={open}
      onClose={onClose}
      footer={
        <Button
          loading={add.isPending}
          onClick={() =>
            void run(async () => {
              await add.mutateAsync({
                name: d.name,
                fromName: d.fromName,
                fromEmail: d.fromEmail,
                mailingAddress: d.mailingAddress,
                steps: [
                  { days: 0, subject: d.s1, body: d.b1 },
                  { days: 3, subject: "", body: d.b2 },
                ],
                dailyCap: Number(d.cap),
                ramp: true,
              });
              onClose();
            }, "Sequence saved")
          }
        >
          Save
        </Button>
      }
    >
      <div className="stack">
        <InputText label="Name" value={d.name} onChange={(e) => f("name")(e.target.value)} />
        <div className="mk-two">
          <InputText label="Sender name" value={d.fromName} onChange={(e) => f("fromName")(e.target.value)} />
          <InputText
            label="Outreach email"
            description="On the separate outreach domain"
            value={d.fromEmail}
            onChange={(e) => f("fromEmail")(e.target.value)}
          />
        </div>
        <InputText
          label="Mailing address"
          description="Every cold email shows it"
          value={d.mailingAddress}
          onChange={(e) => f("mailingAddress")(e.target.value)}
        />
        <InputText label="First email subject" value={d.s1} onChange={(e) => f("s1")(e.target.value)} />
        <TextArea label="First email" value={d.b1} onChange={f("b1")} rows={4} />
        <TextArea label="Follow-up 3 days later" value={d.b2} onChange={f("b2")} rows={3} />
        <InputText
          label="Most a day"
          value={d.cap}
          onChange={(e) => f("cap")(e.target.value)}
          description="Starts at 10 a day and adds 5 each day it sends"
        />
      </div>
    </Modal>
  );
}

function NewProspect({ open, onClose }: { open: boolean; onClose: () => void }) {
  const add = useAddProspect();
  const run = useRun();
  const [d, setD] = useState({ business: "", email: "", website: "", zip: "" });
  return (
    <Modal
      title="Add a business"
      open={open}
      onClose={onClose}
      footer={
        <Button
          loading={add.isPending}
          onClick={() =>
            void run(async () => {
              await add.mutateAsync({ business: d.business, email: d.email, website: d.website || null, zip: d.zip || null });
              setD({ business: "", email: "", website: "", zip: "" });
              onClose();
            }, "Added")
          }
        >
          Add
        </Button>
      }
    >
      <div className="stack">
        <InputText label="Business" value={d.business} onChange={(e) => setD({ ...d, business: e.target.value })} />
        <InputText label="Email" value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} />
        <InputText label="Website" value={d.website} onChange={(e) => setD({ ...d, website: e.target.value })} />
        <InputText
          label="ZIP"
          description="A reply becomes a lead at the location that serves it"
          value={d.zip}
          onChange={(e) => setD({ ...d, zip: e.target.value })}
        />
      </div>
    </Modal>
  );
}

function ReplyModal({ p, onClose }: { p: ProspectRow | null; onClose: () => void }) {
  const reply = useProspectReply();
  const run = useRun();
  const [body, setBody] = useState("");
  return (
    <Modal
      title={p ? `${p.business} replied` : "Reply"}
      open={p !== null}
      onClose={onClose}
      footer={
        <Button
          loading={reply.isPending}
          onClick={() =>
            void run(async () => {
              if (!p) return;
              await reply.mutateAsync({ id: p.id, from: p.email, subject: "Re", body });
              setBody("");
              onClose();
            }, "Saved. A real reply becomes a lead.")
          }
        >
          Save reply
        </Button>
      }
    >
      <div className="stack">
        <p className="muted">
          Paste what they wrote. A stop or a bounce stops every brand from emailing them. A real reply becomes a lead.
        </p>
        <TextArea label="Their reply" value={body} onChange={setBody} rows={4} />
      </div>
    </Modal>
  );
}

const FINDER_WORDS: Record<FinderRow["status"], [string, Status]> = {
  running: ["Running", "informative"],
  done: ["Done", "success"],
  stopped: ["Stopped", "warning"],
};

function Finder({ allowed }: { allowed: boolean }) {
  const { company, can } = useSession();
  const q = useFinder(company.id);
  const find = useFind();
  const run = useRun();
  const [d, setD] = useState({ category: "", area: "", big: false });
  return (
    <Card title="Find businesses">
      <div className="stack">
        <p className="muted">
          Searches Google Maps, reads each website for an email and checks it takes mail. A page is paid for only when it brings
          10 or more new businesses.
        </p>
        {!allowed ? <Banner type="warning">The lead finder is a paid add-on. Ask head office to turn it on.</Banner> : null}
        {can("marketing.update") && allowed ? (
          <div className="mk-two">
            <InputText label="Kind of business" value={d.category} onChange={(e) => setD({ ...d, category: e.target.value })} />
            <InputText label="City or area" value={d.area} onChange={(e) => setD({ ...d, area: e.target.value })} />
            <span className="row">
              <Switch checked={d.big} onChange={(big) => setD({ ...d, big })} label="Big search" /> Big search, up to 2,000
            </span>
            <Button loading={find.isPending} onClick={() => void run(() => find.mutateAsync(d), "Search done")}>
              Find
            </Button>
          </div>
        ) : null}
        {(q.data?.searches ?? []).map((f) => (
          <div key={f.id} className="row between">
            <span>
              <b>{f.category}</b> in {f.area}. {plural(f.added, "business", "businesses")} added, {f.skipped} skipped,{" "}
              {plural(f.pagesPaid, "page")} paid.
              {f.spare > 0 ? (
                <span className="muted"> {plural(f.spare, "business", "businesses")} wait for a free lookup.</span>
              ) : null}
              {f.note ? <span className="muted"> {f.note}</span> : null}
            </span>
            <StatusLabel status={FINDER_WORDS[f.status][1]}>{FINDER_WORDS[f.status][0]}</StatusLabel>
          </div>
        ))}
      </div>
    </Card>
  );
}

export type SuiteTab = "profile" | "ranks" | "ads" | "social" | "email";
export const SUITE_TABS: [SuiteTab, string][] = [
  ["profile", "Google profile"],
  ["ranks", "Website rank"],
  ["ads", "Ads"],
  ["social", "Social"],
  ["email", "Cold email"],
];

export function SuiteTabBody({ tab }: { tab: SuiteTab }): ReactNode {
  if (tab === "profile") return <GoogleProfile />;
  if (tab === "ranks") return <Ranks />;
  if (tab === "ads") return <Ads />;
  if (tab === "social") return <Social />;
  return <ColdEmail />;
}
